import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Header, Footer, Seo } from "../../../components";
import { column } from "../../../store";
import { formatQnaDate } from "../../Courses/[course_id]/courseDetailConfig";
import { FaqHero } from "../FaqHero";
import { parseHashtags, stripHtmlToText } from "../faqPageUtils";
import {
  classifyColumnCategory,
  FAQ_CATEGORY_BY_SLUG,
  DEFAULT_RELATED_COURSE,
} from "../faqCategories";
import { coursePathById } from "../../Courses/courseSlugMap";
import {
  SITE_URL,
  OG_IMAGE,
  ORG_PUBLISHER_NODE,
  canonicalUrl,
  toIso8601,
  firstImageFromHtml,
} from "../../../utils/seo";
import "./FAQDetail.css";

// 상세페이지 하단 "관련 게시글" 최대 노출 수
const RELATED_POSTS_LIMIT = 5;

export const FAQDetail = () => {
  const { column_id } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const fetchedIdRef = useRef(null);

  const { isLoading, columnData, getColumn, allColumns, getAllColumns } =
    column((state) => ({
      isLoading: state.isLoading,
      columnData: state.column,
      getColumn: state.getColumn,
      allColumns: state.allColumns,
      getAllColumns: state.getAllColumns,
    }));

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // column_id별로 한 번만 조회 (StrictMode 이중 실행·리렌더로 인한 조회수 중복 증가 방지)
  useEffect(() => {
    if (fetchedIdRef.current === column_id) return;
    fetchedIdRef.current = column_id;
    getColumn(column_id);
  }, [column_id]);

  // 관련 게시글 계산을 위한 경량 전체 목록(본문 미포함)을 한 번 로드
  useEffect(() => {
    getAllColumns();
  }, [getAllColumns]);

  // 현재 글의 카테고리 판정 → 같은 카테고리의 다른 글을 관련 게시글로 노출.
  // 카테고리를 못 찾으면(공통 절차 안내글) 최신 글로 폴백한다.
  const categorySlug = useMemo(
    () =>
      columnData
        ? classifyColumnCategory(columnData.title, columnData.hashtags)
        : null,
    [columnData]
  );

  const relatedPosts = useMemo(() => {
    if (!columnData || !Array.isArray(allColumns)) return [];
    const currentId = String(columnData.id ?? column_id);
    const others = allColumns.filter(
      (c) => String(c.id) !== currentId
    );
    const sameCategory = categorySlug
      ? others.filter(
          (c) => classifyColumnCategory(c.title, c.hashtags) === categorySlug
        )
      : [];
    // 같은 카테고리 글이 부족하면 최신 글로 채운다.
    const pool = [...sameCategory];
    for (const c of others) {
      if (pool.length >= RELATED_POSTS_LIMIT) break;
      if (!pool.some((p) => String(p.id) === String(c.id))) pool.push(c);
    }
    return pool.slice(0, RELATED_POSTS_LIMIT);
  }, [columnData, allColumns, categorySlug, column_id]);

  // 관련 업무분야(교육과정) — 카테고리에 매핑된 교육과정, 없으면 양형기준 교육과정.
  const relatedCourse = useMemo(() => {
    const cat = categorySlug ? FAQ_CATEGORY_BY_SLUG[categorySlug] : null;
    return cat?.course ?? DEFAULT_RELATED_COURSE;
  }, [categorySlug]);

  const category = categorySlug ? FAQ_CATEGORY_BY_SLUG[categorySlug] : null;

  useEffect(() => {
    if (!contentRef.current || !columnData?.content) return;
    const links = contentRef.current.querySelectorAll("a");
    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      const href = link.getAttribute("href");
      if (href && !/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) {
        link.setAttribute("href", `https://${href}`);
      }
    });
  }, [columnData]);

  const hashtags = useMemo(
    () => parseHashtags(columnData?.hashtags),
    [columnData?.hashtags]
  );

  const seo = useMemo(() => {
    if (!columnData) return null;

    const plainContent = stripHtmlToText(columnData.content);
    const description = plainContent
      ? `${plainContent.slice(0, 155)}${plainContent.length > 155 ? "…" : ""}`
      : `${columnData.title} - 희망과학 심리상담센터 자주 묻는 질문`;
    const canonical = canonicalUrl(`/faq/${column_id}`);
    const articleImage = firstImageFromHtml(columnData.content);
    const publishedAt = toIso8601(columnData.created_at);
    const modifiedAt =
      columnData.updated_at && columnData.updated_at !== columnData.created_at
        ? toIso8601(columnData.updated_at)
        : undefined;

    // 정보성 칼럼이므로 FAQPage가 아니라 Article + BreadcrumbList 로 표현한다.
    const article = {
      "@type": "Article",
      "@id": `${canonical}#article`,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      url: canonical,
      headline: columnData.title,
      description,
      inLanguage: "ko-KR",
      author: ORG_PUBLISHER_NODE,
      publisher: ORG_PUBLISHER_NODE,
      ...(plainContent ? { articleBody: plainContent } : {}),
      ...(publishedAt ? { datePublished: publishedAt } : {}),
      ...(modifiedAt ? { dateModified: modifiedAt } : {}),
      ...(hashtags.length > 0 ? { keywords: hashtags } : {}),
      ...(articleImage ? { image: [articleImage] } : {}),
    };

    // 카테고리를 판정할 수 있으면 빵부스러기에 카테고리 단계를 끼워 넣는다.
    const crumbs = [
      { "@type": "ListItem", position: 1, name: "홈", item: `${SITE_URL}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "자주 묻는 질문",
        item: `${SITE_URL}/faq`,
      },
    ];
    if (category) {
      crumbs.push({
        "@type": "ListItem",
        position: crumbs.length + 1,
        name: `${category.name} 법률정보`,
        item: `${SITE_URL}/faq/category/${category.slug}`,
      });
    }
    crumbs.push({
      "@type": "ListItem",
      position: crumbs.length + 1,
      name: columnData.title,
      item: canonical,
    });

    const breadcrumb = {
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: crumbs,
    };

    // 요청사항: 게시글 제목 + 핵심 키워드(카테고리)를 포함한 고유 title.
    const title = category
      ? `${columnData.title} | ${category.name} 법률정보 - 희망과학`
      : `${columnData.title} | 희망과학 자주 묻는 질문`;

    return {
      title,
      description,
      canonical,
      image: articleImage || OG_IMAGE,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [article, breadcrumb],
      },
    };
  }, [columnData, column_id, hashtags, category]);

  return (
    <div className="faq-detail-page-shell">
      {seo && (
        <Seo
          title={seo.title}
          description={seo.description}
          canonical={seo.canonical}
          type="article"
          image={seo.image}
          jsonLd={seo.jsonLd}
        />
      )}

      <Header
        variant={isMobile ? "solid" : "dark"}
        mobileTitle={isMobile ? "자주 묻는 질문" : undefined}
        onMobileClose={isMobile ? () => navigate("/faq") : undefined}
      />

      <main className="faq-detail-page">
        <FaqHero asHeading={false} />

        <div className="faq-detail-page__content">
          <div className="faq-detail-page__inner">
            {isLoading ? (
              <p className="faq-detail-page__status">불러오는 중...</p>
            ) : !columnData ? (
              <p className="faq-detail-page__status">
                질문을 찾을 수 없습니다.
              </p>
            ) : (
              <>
                <article className="faq-detail-article">
                  <header className="faq-detail-article__header">
                    <h1 className="faq-detail-article__title">
                      {columnData.title}
                    </h1>
                    <div className="faq-detail-article__meta">
                      <time dateTime={columnData.created_at}>
                        {formatQnaDate(columnData.created_at)}
                      </time>
                      {columnData.updated_at &&
                        columnData.updated_at !== columnData.created_at && (
                          <>
                            <span
                              className="faq-detail-article__meta-divider"
                              aria-hidden
                            >
                              |
                            </span>
                            <span>
                              수정 {formatQnaDate(columnData.updated_at)}
                            </span>
                          </>
                        )}
                      <span
                        className="faq-detail-article__meta-divider"
                        aria-hidden
                      >
                        |
                      </span>
                      <span>조회수 : {columnData.view_count ?? 0}</span>
                    </div>
                  </header>

                  <div
                    ref={contentRef}
                    className="faq-detail-article__body faq-detail-article__body--html"
                    dangerouslySetInnerHTML={{ __html: columnData.content }}
                  />

                  {hashtags.length > 0 && (
                    <div className="faq-detail-article__tags">
                      {hashtags.map((tag, idx) => (
                        <span key={idx} className="faq-detail-article__tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </article>

                {/* 관련 업무분야(교육과정) */}
                {relatedCourse && (
                  <aside
                    className="faq-detail-related-course"
                    aria-label="관련 업무분야"
                  >
                    <p className="faq-detail-related-course__label">
                      관련 업무분야
                    </p>
                    <Link
                      to={coursePathById(relatedCourse.id)}
                      className="faq-detail-related-course__link"
                    >
                      <span className="faq-detail-related-course__name">
                        {relatedCourse.name}
                      </span>
                      <span
                        className="faq-detail-related-course__cta"
                        aria-hidden
                      >
                        교육 안내 보기 →
                      </span>
                    </Link>
                  </aside>
                )}

                {/* 관련 게시글 */}
                {relatedPosts.length > 0 && (
                  <section
                    className="faq-detail-related"
                    aria-label="관련 게시글"
                  >
                    <h2 className="faq-detail-related__title">
                      {category ? `${category.name} 관련 게시글` : "관련 게시글"}
                    </h2>
                    <ul className="faq-detail-related__list">
                      {relatedPosts.map((post) => (
                        <li
                          key={post.id}
                          className="faq-detail-related__item"
                        >
                          <Link
                            to={`/faq/${post.id}`}
                            className="faq-detail-related__link"
                          >
                            <span
                              className="faq-detail-related__q"
                              aria-hidden
                            >
                              Q
                            </span>
                            <span className="faq-detail-related__text">
                              {post.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div className="faq-detail-actions">
                  {category && (
                    <Link
                      to={`/faq/category/${category.slug}`}
                      className="faq-detail-actions__category"
                    >
                      {category.name} 법률정보 더보기
                    </Link>
                  )}
                  <Link to="/faq" className="faq-detail-actions__list">
                    목록보기
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
